import React, { useEffect, useRef } from 'react';
import { useDescope } from '@descope/react-sdk';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import {
  SagaProgressMolecule,
  PrimeReactButton as ButtonAtom,
} from 'design-system/my-lib';
import type { EmbeddedSagaProgress } from 'types/sagaProgress';
import './OrganizationProvisioningScreen.scss';

type ProvisioningStatus = 'deploying' | 'ready' | 'failed';

export interface OrganizationProvisioningScreenProps {
  status: ProvisioningStatus | null;
  progress: EmbeddedSagaProgress | null;
  /** The foundation run's saga run id — the support reference, the same id the progress molecule prints. */
  runId: string | null;
  /** The auto-provisioned organization's display name, once known — see `useAppInitialization`. */
  organizationName?: string | null;
  /** The latest `provisioning-status` poll failure, while polls keep failing; `null` after any success. */
  pollError?: Error | null;
  /** True once `provisioning-status` itself has failed repeatedly — the screen can no longer say setup is running. */
  unreachable?: boolean;
  /** True when `provisioning-status` answered 404: the organization is not in this environment. Trying again cannot help. */
  notFound?: boolean;
  /** Re-polls `provisioning-status` immediately. */
  onRetry?: () => void;
  /** Called when the user acknowledges the `ready` confirmation and wants to enter their organization. */
  onContinue?: () => void;
}

/**
 * Stands in for the progress detail while `provisioning-status` answers without it — organization
 * -service could not reach graph-service (D3). Setup is still running; the molecule says so and
 * offers *Retry now* rather than showing textless skeleton rows for the whole wait.
 */
const PROGRESS_UNAVAILABLE = new Error('provisioning-status returned no progress detail');

/**
 * `org_provisioning_waited` — how long a user watched their organization deploy and how the wait
 * ended. Only a wait the user actually saw is reported: a screen that opens on an organization that
 * already failed (a returning user) had no wait. Leaving before an outcome — signing out, closing
 * the tab — is `abandoned`.
 */
function useProvisioningWaitAnalytics(status: ProvisioningStatus | null): void {
  const logAnalyticsEvent = useAnalytics();
  const logRef = useRef(logAnalyticsEvent);
  logRef.current = logAnalyticsEvent;
  const waitStartedAtRef = useRef<number | null>(status === 'deploying' ? Date.now() : null);

  const reportRef = useRef((outcome: 'ready' | 'failed' | 'abandoned') => {
    const startedAt = waitStartedAtRef.current;
    if (startedAt == null) return;
    waitStartedAtRef.current = null;
    logRef.current('org_provisioning_waited', undefined, { waitMs: Date.now() - startedAt, outcome });
  });

  useEffect(() => {
    if (status === 'ready' || status === 'failed') reportRef.current(status);
  }, [status]);

  useEffect(() => {
    const report = reportRef.current;
    const onPageHide = () => report('abandoned');
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      report('abandoned');
    };
  }, []);
}

/**
 * Shown while the just-signed-up user's organization is still deploying its
 * foundation approach (see `useAppInitialization`'s `provisioning-organization`
 * phase). Mirrors `LoginPage` — a centered panel, no chrome, since the user has
 * no org yet to render the shell around. Once the organization reports `ready`,
 * this screen holds on a one-line confirmation naming the organization (XP1 —
 * a clear statement, not a silent redirect) until the user continues.
 *
 * *Sign out* is available in every state: the deployment runs server-side and
 * continues without the user, so leaving is never destructive, and a stalled
 * deployment must not strand anyone on this screen.
 */
const OrganizationProvisioningScreen: React.FC<OrganizationProvisioningScreenProps> = ({
  status,
  progress,
  runId,
  organizationName = null,
  pollError = null,
  unreachable = false,
  notFound = false,
  onRetry,
  onContinue,
}) => {
  const { t } = useTranslation();
  const { logout } = useDescope();
  const logAnalyticsEvent = useAnalytics();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useProvisioningWaitAnalytics(status);

  // The whole view changes with the status — working, ready, failed — so focus returns to the
  // heading, which names the new state. A live region mounted together with its content is not
  // reliably announced; a focused heading is.
  useEffect(() => {
    headingRef.current?.focus();
  }, [status]);

  const isFailed = status === 'failed';
  const isReady = status === 'ready';
  const signOutButton = (variant: 'secondary' | 'text') => (
    <ButtonAtom
      label={t('appInit.provisioning.signOut')}
      variant={variant}
      data-testid="provisioning-sign-out"
      onClick={() => logout()}
    />
  );

  const renderFailed = () => {
    // With the run's own outcome in hand, the molecule gives the full account — what was built,
    // what was undone, what never started — plus the "nothing else is needed" line and the
    // support reference. Without it, the screen says the same thing in one line.
    const hasOutcome = progress?.outcome != null;
    return (
      <div className="org-provisioning-screen__failed">
        {hasOutcome ? (
          <SagaProgressMolecule variant="block" surface="signup" progress={progress} loading={false} error={null} />
        ) : (
          <p className="org-provisioning-screen__message">
            {runId
              ? t('appInit.provisioning.failedDescription', { runId })
              : t('appInit.provisioning.failedDescriptionNoReference')}
          </p>
        )}
        <div className="org-provisioning-screen__actions">{signOutButton('secondary')}</div>
      </div>
    );
  };

  const renderReady = () => (
    <div className="org-provisioning-screen__ready">
      <p className="org-provisioning-screen__message">
        {organizationName
          ? t('appInit.provisioning.readyDescriptionNamed', { organizationName })
          : t('appInit.provisioning.readyDescription')}
      </p>
      <div className="org-provisioning-screen__actions">
        <ButtonAtom
          label={t('appInit.provisioning.continueButton')}
          onClick={() => {
            logAnalyticsEvent('organization_ready_confirmed');
            onContinue?.();
          }}
        />
      </div>
    </div>
  );

  const renderUnreachable = () => (
    <div className="org-provisioning-screen__unreachable" role="alert" data-testid="provisioning-unreachable">
      <p className="org-provisioning-screen__message">{t('appInit.provisioning.unreachableDescription')}</p>
      <div className="org-provisioning-screen__actions">
        {onRetry && <ButtonAtom label={t('appInit.provisioning.retry')} onClick={onRetry} />}
        {signOutButton('secondary')}
      </div>
    </div>
  );

  const renderNotFound = () => (
    <div className="org-provisioning-screen__unreachable" role="alert" data-testid="provisioning-not-found">
      <p className="org-provisioning-screen__message">{t('appInit.provisioning.notFoundDescription')}</p>
      <div className="org-provisioning-screen__actions">{signOutButton('secondary')}</div>
    </div>
  );

  const renderDeploying = () => (
    <>
      <SagaProgressMolecule
        variant="block"
        surface="signup"
        progress={progress}
        loading={false}
        error={progress ? pollError : PROGRESS_UNAVAILABLE}
        onRetry={onRetry}
      />
      <div className="org-provisioning-screen__leave">
        <p className="org-provisioning-screen__leave-hint">{t('appInit.provisioning.signOutWhileWorking')}</p>
        {signOutButton('text')}
      </div>
    </>
  );

  const renderBody = () => {
    if (isFailed) return renderFailed();
    if (isReady) return renderReady();
    if (notFound) return renderNotFound();
    if (unreachable) return renderUnreachable();
    return renderDeploying();
  };

  let headingKey = 'appInit.provisioning.heading';
  if (isFailed) headingKey = 'appInit.provisioning.failedTitle';
  else if (isReady) headingKey = 'appInit.provisioning.readyTitle';

  return (
    <main className="org-provisioning-screen">
      <div className="org-provisioning-screen__panel">
        <h1 ref={headingRef} tabIndex={-1} className="org-provisioning-screen__heading">
          {t(headingKey)}
        </h1>
        {renderBody()}
      </div>
    </main>
  );
};

export default OrganizationProvisioningScreen;
